import { useCallback } from 'react';
import { useDockviewApi } from './dockview-context.js';
import { useAppStore, useActiveTabId, type Selection } from './store.js';
import type { PanelComponentKey, PanelParams } from './components.js';

// Opens a Home record focused on the given home inside the active workspace
// tab — the twin of useOpenCompanyOverview for the care-home grain.
//
// If a Home record already exists in the tab, reuse it: push the home into
// whatever that panel follows (its channel, or its own pin) and bring it
// forward. Otherwise add one as a floating panel on the default channel.
// Either way the selection is a `kind: 'home'` one, so a linked operator
// Record also moves to the home's operator (store deriveEntityId).
//
// Used by the home-row context menu ("Open home") and Homes double-click.

export interface HomeTarget {
  homeKey: string;
  name?: string | null;
  operatorEntityId?: string | null;
}

export function homeSelection(t: HomeTarget): Selection {
  return {
    kind: 'home',
    key: t.homeKey,
    ...(t.name ? { label: t.name } : {}),
    operatorEntityId: t.operatorEntityId ?? null,
  };
}

export function useOpenHomeRecord(): (target: HomeTarget) => void {
  const activeTabId = useActiveTabId();
  const apiRef = useDockviewApi();
  const addPanelToStore = useAppStore((s) => s.actions.addPanel);
  const setPanelSelection = useAppStore((s) => s.actions.setPanelSelection);

  return useCallback(
    (target: HomeTarget) => {
      if (!activeTabId) return;
      const api = apiRef.current;
      if (!api) return;
      const sel = homeSelection(target);

      const existing = api.panels.find((p) => (p.api.component as string) === 'home-record');
      if (existing) {
        setPanelSelection(activeTabId, existing.id, sel);
        existing.api.setActive();
        return;
      }

      const newPanelId = addPanelToStore(activeTabId, {
        component: 'home-record',
        title: 'Home record',
      });
      const cascade = api.panels.filter((p) => p.api.location.type === 'floating').length;
      const offset = 24 * cascade;
      api.addPanel<PanelParams>({
        id: newPanelId,
        component: 'home-record' as PanelComponentKey,
        title: 'Home record',
        params: { tabId: activeTabId, panelId: newPanelId },
        floating: {
          position: { left: 140 + offset, top: 80 + offset },
          width: 560,
          height: 620,
        },
      });
      setPanelSelection(activeTabId, newPanelId, sel);
    },
    [activeTabId, apiRef, addPanelToStore, setPanelSelection],
  );
}
